import { useState, useRef } from "react";
import { X, ShieldCheck, ShieldAlert } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

// Sender chip input — Feb 2026 UX refit. Replaces the fragile
// comma-separated free-text field on the Email Intake mailbox card.
//
// Each entry is a removable pill; invisible characters (non-breaking
// spaces from copy-paste, smart quotes, zero-width chars) get stripped
// during add, and each entry is validated as either `email@domain` or
// a whole-domain suffix `@domain`. There is no way to save a broken
// pattern by accident.
//
// Entries are stored as a plain string[] so the parent component can
// hand it straight to the backend `sender_allowlist: list[str]` field
// with no split/join round-tripping.

const EMAIL_RE = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
const DOMAIN_RE = /^@[a-z0-9.-]+\.[a-z]{2,}$/i;

function sanitiseEntry(raw) {
  // Strip every unicode whitespace variant + zero-width chars + smart
  // quotes that copy-paste from Outlook / Word / PDF love to inject.
  // Then lowercase for canonical storage.
  return (raw || "")
    .replace(/[\u00A0\u1680\u2000-\u200D\u2028\u2029\u202F\u205F\u3000\uFEFF]/g, "")
    .replace(/["'\u2018\u2019\u201C\u201D]/g, "")
    .trim()
    .toLowerCase();
}

function isValidPattern(s) {
  return EMAIL_RE.test(s) || DOMAIN_RE.test(s);
}

export function SenderChipInput({
  value,
  onChange,
  placeholder = "email@domain or @domain",
  testid,
  disabled = false,
  // Feb 2026 — optional custom validator for free-form chip lists
  // (e.g. ignore-phrases). Defaults to the sender email/domain
  // validator. Return true = accept; false = reject with the shared
  // "needs email@domain..." hint. Pass a custom hint via `validateHint`.
  validate = isValidPattern,
  validateHint = "needs to look like email@domain.com or @domain.com",
}) {
  const [buffer, setBuffer] = useState("");
  const [err, setErr] = useState("");
  const inputRef = useRef(null);
  const items = Array.isArray(value) ? value : [];

  // Add one or more entries — accepts a comma-separated batch (so a
  // paste of "a@x.com, b@y.com" adds two chips in one go), sanitises
  // each, validates each, dedups against the current list, and returns
  // the merged array + a message about anything that got rejected.
  const commit = (raw) => {
    const pieces = String(raw || "")
      .split(/[,\n\r]+/)
      .map(sanitiseEntry)
      .filter(Boolean);
    if (!pieces.length) {
      setBuffer("");
      return;
    }
    const seen = new Set(items);
    const added = [];
    const rejected = [];
    for (const p of pieces) {
      if (seen.has(p)) continue;
      if (!validate(p)) {
        rejected.push(p);
        continue;
      }
      seen.add(p);
      added.push(p);
    }
    if (added.length) {
      onChange([...items, ...added]);
    }
    if (rejected.length) {
      setErr(`Skipped: ${rejected.join(", ")} — ${validateHint}`);
    } else {
      setErr("");
    }
    setBuffer("");
  };

  const remove = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(buffer);
    } else if (e.key === "Backspace" && !buffer && items.length) {
      // Backspace on an empty input peels the last chip — makes fast
      // corrections feel natural on the keyboard.
      remove(items.length - 1);
    }
  };

  return (
    <div>
      <div
        className={cn(
          "min-h-[38px] w-full rounded-md border border-input bg-transparent " +
          "px-2 py-1.5 flex flex-wrap gap-1.5 items-center " +
          "focus-within:ring-1 focus-within:ring-ring transition-shadow",
          disabled && "opacity-50 pointer-events-none",
        )}
        onClick={() => inputRef.current?.focus()}
        data-testid={testid}
      >
        {items.map((entry, idx) => (
          <span
            key={`${entry}-${idx}`}
            className={cn(
              "inline-flex items-center gap-1 rounded-full pl-2 pr-1 py-0.5 " +
              "text-xs font-medium",
              entry.startsWith("@")
                ? "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
                : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200",
            )}
            data-testid={`${testid}-chip-${idx}`}
            title={entry.startsWith("@")
              ? `Domain rule — matches every ${entry.slice(1)} sender`
              : "Exact-address rule"}
          >
            <span>{entry}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); remove(idx); }}
              className="rounded-full hover:bg-black/10 dark:hover:bg-white/10 p-0.5"
              aria-label={`Remove ${entry}`}
              data-testid={`${testid}-chip-remove-${idx}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={buffer}
          onChange={(e) => setBuffer(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => buffer.trim() && commit(buffer)}
          onPaste={(e) => {
            const text = e.clipboardData?.getData("text");
            if (text && /[,\n]/.test(text)) {
              e.preventDefault();
              commit(text);
            }
          }}
          placeholder={items.length ? "" : placeholder}
          className={cn(
            "flex-1 min-w-[10rem] bg-transparent outline-none " +
            "text-sm py-0.5 placeholder:text-muted-foreground",
          )}
          data-testid={`${testid}-input`}
          disabled={disabled}
        />
      </div>
      {err && (
        <div className="mt-1 text-xs text-rose-600 dark:text-rose-400" data-testid={`${testid}-error`}>
          {err}
        </div>
      )}
    </div>
  );
}


// Mirrors the backend `intake_mailbox._sender_domain_matches` EXACTLY
// so what the tester says matches what the poller will actually do.
// If you change the backend rule, change this in the same commit.
export function testSenderAgainstPatterns(rawSender, patterns) {
  const s = sanitiseEntry(rawSender);
  if (!s) return { matched: false, reason: "empty sender" };
  const list = Array.isArray(patterns) ? patterns : [];
  for (const p of list) {
    const p_low = sanitiseEntry(p);
    if (!p_low) continue;
    if (p_low.startsWith("@") && s.endsWith(p_low)) {
      return { matched: true, reason: `domain rule ${p_low}` };
    }
    if (p_low === s) {
      return { matched: true, reason: `exact rule ${p_low}` };
    }
  }
  return { matched: false, reason: "no pattern matched" };
}


// Small inline tool under the allowlist field — dispatcher types a
// sender and sees green/red immediately, without needing a real
// incoming email to arrive. Uses the identical match logic as the
// production poller (see `testSenderAgainstPatterns` above).
export function SenderMatchTester({ allowlist, blocklist, testid }) {
  const [sender, setSender] = useState("");
  const [result, setResult] = useState(null);
  const trimmed = sender.trim();

  const check = () => {
    if (!trimmed) {
      setResult(null);
      return;
    }
    const blocked = testSenderAgainstPatterns(trimmed, blocklist || []);
    if (blocked.matched) {
      setResult({
        pass: false,
        title: "Would be BLOCKED",
        detail: `Sender matches ${blocked.reason} in the blocklist — blocklist wins over allowlist.`,
      });
      return;
    }
    const allowed = testSenderAgainstPatterns(trimmed, allowlist || []);
    if (allowed.matched) {
      setResult({
        pass: true,
        title: "Would be ACCEPTED",
        detail: `Sender matches ${allowed.reason}.`,
      });
    } else {
      setResult({
        pass: false,
        title: "Would be SKIPPED",
        detail: (allowlist || []).length
          ? "Sender is not in the allowlist. Add either the exact address or a @domain rule."
          : "Allowlist is empty — fail-closed. Add at least one pattern before enabling this mailbox.",
      });
    }
  };

  return (
    <div className="mt-2 p-2.5 rounded-md bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
      <div className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1.5">
        Test a sender — does it pass this mailbox&apos;s rules?
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={sender}
          onChange={(e) => { setSender(e.target.value); setResult(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); check(); } }}
          placeholder="e.g. emma@nzra.co.nz"
          className="h-8 text-sm"
          data-testid={`${testid}-input`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={check}
          disabled={!trimmed}
          className="h-8"
          data-testid={`${testid}-check`}
        >
          Check
        </Button>
      </div>
      {result && (
        <div
          className={cn(
            "mt-2 flex items-start gap-2 text-xs rounded-md px-2.5 py-2",
            result.pass
              ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
              : "bg-rose-50 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200",
          )}
          data-testid={`${testid}-result`}
        >
          {result.pass ? (
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
          ) : (
            <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
          )}
          <div>
            <div className="font-semibold">{result.title}</div>
            <div className="opacity-90">{result.detail}</div>
          </div>
        </div>
      )}
    </div>
  );
}
